import { memo, useMemo } from 'react'
import { Box, Chip, Collapse, Paper, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  ValidationResult,
  ValidationSeverity,
  countBySeverity,
} from './SchedulerValidation.utils'

// ─── Constants ──────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<
  ValidationSeverity,
  { icon: typeof ErrorOutlineIcon; color: string; bg: string; label: string }
> = {
  error: {
    icon: ErrorOutlineIcon,
    color: '#d32f2f',
    bg: 'rgba(211, 47, 47, 0.06)',
    label: 'Error',
  },
  warning: {
    icon: WarningAmberIcon,
    color: '#ed6c02',
    bg: 'rgba(237, 108, 2, 0.06)',
    label: 'Warning',
  },
  info: {
    icon: InfoOutlinedIcon,
    color: '#0288d1',
    bg: 'rgba(2, 136, 209, 0.06)',
    label: 'Info',
  },
}

// ─── Components ─────────────────────────────────────────────────────

const ValidationItem = memo(({ result }: { result: ValidationResult }) => {
  const config = SEVERITY_CONFIG[result.severity]
  const Icon = config.icon

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: 0.75,
        px: 1.5,
        bgcolor: config.bg,
        borderRadius: '4px',
        borderLeft: `3px solid ${config.color}`,
      }}
    >
      <Icon
        sx={{ fontSize: 16, color: config.color, mt: '1px', flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 600, color: config.color }}
        >
          {result.message}
        </Typography>
        {result.detail && (
          <Typography
            sx={{
              fontSize: '0.65rem',
              color: 'text.secondary',
              mt: 0.25,
              wordBreak: 'break-word',
            }}
          >
            {result.detail}
          </Typography>
        )}
      </Box>
    </Box>
  )
})
ValidationItem.displayName = 'ValidationItem'

// ─── Main Component ─────────────────────────────────────────────────

type SchedulerValidationProps = {
  results: ValidationResult[]
  isLoading?: boolean
}

export const SchedulerValidationPanel = memo(
  ({ results, isLoading }: SchedulerValidationProps) => {
    const counts = useMemo(() => countBySeverity(results), [results])
    const hasIssues = results.length > 0

    if (isLoading) {
      return (
        <Paper
          variant="outlined"
          sx={{ p: 2, borderRadius: '6px', textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Validating schedule data...
          </Typography>
        </Paper>
      )
    }

    if (!hasIssues) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(46, 125, 50, 0.04)',
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
          <Typography
            sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2e7d32' }}
          >
            All checks passed — ready to run
          </Typography>
        </Paper>
      )
    }

    // Group results by severity
    const grouped: Record<ValidationSeverity, ValidationResult[]> = {
      error: results.filter((r) => r.severity === 'error'),
      warning: results.filter((r) => r.severity === 'warning'),
      info: results.filter((r) => r.severity === 'info'),
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Summary badges */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {counts.error > 0 && (
            <Chip
              icon={<ErrorOutlineIcon sx={{ fontSize: '14px !important' }} />}
              label={`${counts.error} error${counts.error !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 24,
                color: '#d32f2f',
                bgcolor: 'rgba(211, 47, 47, 0.08)',
                borderColor: 'rgba(211, 47, 47, 0.3)',
                '& .MuiChip-icon': { color: '#d32f2f' },
              }}
              variant="outlined"
            />
          )}
          {counts.warning > 0 && (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
              label={`${counts.warning} warning${counts.warning !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 24,
                color: '#ed6c02',
                bgcolor: 'rgba(237, 108, 2, 0.08)',
                borderColor: 'rgba(237, 108, 2, 0.3)',
                '& .MuiChip-icon': { color: '#ed6c02' },
              }}
              variant="outlined"
            />
          )}
          {counts.info > 0 && (
            <Chip
              icon={<InfoOutlinedIcon sx={{ fontSize: '14px !important' }} />}
              label={`${counts.info} info`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 24,
                color: '#0288d1',
                bgcolor: 'rgba(2, 136, 209, 0.08)',
                borderColor: 'rgba(2, 136, 209, 0.3)',
                '& .MuiChip-icon': { color: '#0288d1' },
              }}
              variant="outlined"
            />
          )}
        </Box>

        {/* Grouped results */}
        {(['error', 'warning', 'info'] as ValidationSeverity[]).map(
          (severity) => (
            <Collapse key={severity} in={grouped[severity].length > 0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {grouped[severity].map((result) => (
                  <ValidationItem key={result.id} result={result} />
                ))}
              </Box>
            </Collapse>
          )
        )}
      </Box>
    )
  }
)

SchedulerValidationPanel.displayName = 'SchedulerValidationPanel'
